import React from "react";
import AppErrorBoundary from "./components/AppErrorBoundary";
import NotFoundPage from "./pages/NotFoundPage";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ModeProvider } from "./contexts/ModeContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import { initNativePlatform } from "./lib/native-init";

// Initialize native platform features (status bar, splash screen, back button)
initNativePlatform();
import ProtectedRoute from "./components/ProtectedRoute";
import FeatureGate from "./components/FeatureGate";
import LandingPage from "./pages/LandingPage";
import SharedCalendarDay from "./pages/SharedCalendarDay";
import BookDemoPage from "./pages/BookDemoPage";
import ThankYouPage from "./pages/ThankYouPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import CompanyRegisterPage from "./pages/CompanyRegisterPage";
import PackageSelectionPage from "./pages/PackageSelectionPage";
import CompleteRegistrationPage from "./pages/CompleteRegistrationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import BillingPage from "./pages/app/BillingPage";
import AppLayout from "./layouts/AppLayout";
import OperatorLayout from "./layouts/OperatorLayout";
import ModeRedirect from "./components/ModeRedirect";
import DashboardOverview from "./pages/app/DashboardOverview";
import DemoRequestsPage from "./pages/app/DemoRequestsPage";
import WaitlistPage from "./pages/app/WaitlistPage";
import BugReportsAdminPage from "./pages/app/BugReportsAdminPage";
import WindrowsPage from "./pages/app/WindrowsPage";
import WindrowDetailPage from "./pages/app/WindrowDetailPage";
import LogTemperaturePage from "./pages/app/LogTemperaturePage";
import LogTurnPage from "./pages/app/LogTurnPage";
import RecipesPage from "./pages/app/RecipesPage";
import ReportsPage from "./pages/app/ReportsPage";
import SettingsPage from "./pages/app/SettingsPage";
import OperationsManagementPage from "./pages/app/OperationsManagementPage";
import OperatorDashboard from "./pages/operator/OperatorDashboard";
import OperatorWindrowDetail from "./pages/operator/OperatorWindrowDetail";
import OperatorWindrowLocation from "./pages/operator/OperatorWindrowLocation";
import LogMaterialPage from "./pages/app/LogMaterialPage";
import OperatorStatus from "./pages/operator/OperatorStatus";

// Operations module pages
import BolList from "./pages/app/ops/BolList";
import CreateBol from "./pages/app/ops/CreateBol";
import ViewBol from "./pages/app/ops/ViewBol";
import EditBol from "./pages/app/ops/EditBol";
import CodList from "./pages/app/ops/CodList";
import CreateCod from "./pages/app/ops/CreateCod";
import ViewCod from "./pages/app/ops/ViewCod";
import WorkOrderList from "./pages/app/ops/WorkOrderList";
import OrderIntakeQueue from "./pages/app/ops/OrderIntakeQueue";
import CreateWorkOrder from "./pages/app/ops/CreateWorkOrder";
import ViewWorkOrder from "./pages/app/ops/ViewWorkOrder";
import TaskBoard from "./pages/app/ops/TaskBoard";
import OpsCalendar from "./pages/app/ops/Calendar";
import OpsResources from "./pages/app/ops/Resources";
import OpsSettings from "./pages/app/ops/OpsSettings";
import OpsClients from "./pages/app/ops/Clients";
import JobsList from "./pages/app/ops/JobsList";
import ViewJob from "./pages/app/ops/ViewJob";
import OpsProducts from "./pages/app/ops/Products";
import OpsInventory from "./pages/app/ops/Inventory";
import IncidentReports from "./pages/app/ops/IncidentReports";
import TeamPage from "./pages/app/TeamPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import CustomerSchedulePage from "./pages/CustomerSchedulePage";
import EnterpriseSampleApp from "./components/demo/EnterpriseSampleApp";

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <OfflineProvider>
            <ModeProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/book-demo" element={<BookDemoPage />} />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/register/company" element={<CompanyRegisterPage />} />
                <Route path="/register/package" element={<PackageSelectionPage />} />
                <Route path="/register/complete" element={<CompleteRegistrationPage />} />
                <Route path="/invite/:token" element={<AcceptInvitePage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/share/calendar/:orgId/:date" element={<SharedCalendarDay />} />
                <Route path="/schedule/:token" element={<CustomerSchedulePage />} />
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <ModeRedirect />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardOverview />} />
                  <Route path="demo-requests" element={<FeatureGate feature="platform_admin"><DemoRequestsPage /></FeatureGate>} />
                  <Route path="waitlist" element={<FeatureGate feature="platform_admin"><WaitlistPage /></FeatureGate>} />
                  <Route path="feedback" element={<FeatureGate feature="admin"><BugReportsAdminPage /></FeatureGate>} />
                  <Route path="windrows" element={<WindrowsPage />} />
                  <Route path="windrows/:id" element={<WindrowDetailPage />} />
                  <Route path="log-temp" element={<LogTemperaturePage />} />
                  <Route path="log-turn" element={<LogTurnPage />} />
                  <Route path="log-material" element={<LogMaterialPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  {import.meta.env.VITE_LOCAL_DEMO === "true" && <><Route path="recipes" element={<RecipesPage />} /><Route path="ingredients" element={<RecipesPage />} /><Route path="enterprise/*" element={<EnterpriseSampleApp />} /></>}
                  <Route path="operations" element={<FeatureGate feature="platform_admin"><OperationsManagementPage /></FeatureGate>} />
                  <Route path="team" element={<FeatureGate feature="admin"><TeamPage /></FeatureGate>} />
                  <Route path="settings" element={<FeatureGate feature="admin"><SettingsPage /></FeatureGate>} />
                  <Route path="billing" element={<FeatureGate feature="admin"><BillingPage /></FeatureGate>} />

                  {/* Operations module */}
                  <Route path="ops" element={<Navigate to="/app" replace />} />
                  <Route path="ops/intake-queue" element={<FeatureGate feature="operations"><OrderIntakeQueue /></FeatureGate>} />
                  <Route path="ops/bols" element={<FeatureGate feature="operations"><BolList /></FeatureGate>} />
                  <Route path="ops/bols/new" element={<FeatureGate feature="operations"><CreateBol /></FeatureGate>} />
                  <Route path="ops/bols/:id" element={<FeatureGate feature="operations"><ViewBol /></FeatureGate>} />
                  <Route path="ops/bols/:id/edit" element={<FeatureGate feature="operations"><EditBol /></FeatureGate>} />
                  <Route path="ops/cods" element={<FeatureGate feature="operations"><CodList /></FeatureGate>} />
                  <Route path="ops/cods/new" element={<FeatureGate feature="operations"><CreateCod /></FeatureGate>} />
                  <Route path="ops/cods/:id" element={<FeatureGate feature="operations"><ViewCod /></FeatureGate>} />
                  <Route path="ops/jobs" element={<FeatureGate feature="operations"><JobsList /></FeatureGate>} />
                  <Route path="ops/jobs/:id" element={<FeatureGate feature="operations"><ViewJob /></FeatureGate>} />
                  <Route path="ops/work-orders" element={<FeatureGate feature="operations"><WorkOrderList /></FeatureGate>} />
                  <Route path="ops/work-orders/new" element={<FeatureGate feature="operations"><CreateWorkOrder /></FeatureGate>} />
                  <Route path="ops/work-orders/:id" element={<FeatureGate feature="operations"><ViewWorkOrder /></FeatureGate>} />
                  <Route path="ops/tasks" element={<FeatureGate feature="operations"><TaskBoard /></FeatureGate>} />
                  <Route path="ops/clients" element={<FeatureGate feature="operations"><OpsClients /></FeatureGate>} />
                  <Route path="ops/products" element={<FeatureGate feature="operations"><OpsProducts /></FeatureGate>} />
                  <Route path="ops/products/ingredients/:ingredientId" element={<FeatureGate feature="operations"><OpsProducts /></FeatureGate>} />
                  <Route path="ops/products/:productId" element={<FeatureGate feature="operations"><OpsProducts /></FeatureGate>} />
                  <Route path="ops/inventory" element={<FeatureGate feature="operations"><OpsInventory /></FeatureGate>} />
                  <Route path="ops/calendar" element={<FeatureGate feature="operations"><OpsCalendar /></FeatureGate>} />
                  <Route path="ops/resources" element={<FeatureGate feature="operations"><OpsResources /></FeatureGate>} />
                  <Route path="ops/incidents" element={<FeatureGate feature="operations"><IncidentReports /></FeatureGate>} />
                  <Route path="ops/settings" element={<FeatureGate feature="operations"><OpsSettings /></FeatureGate>} />
                </Route>
                <Route
                  path="/app/operator"
                  element={
                    <ProtectedRoute>
                      <OperatorLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<OperatorDashboard />} />
                  <Route path="windrows/:id" element={<OperatorWindrowDetail />} />
                  <Route path="windrows/:id/location" element={<OperatorWindrowLocation />} />
                  <Route path="log-temp" element={<LogTemperaturePage />} />
                  <Route path="log-turn" element={<LogTurnPage />} />
                  <Route path="log-material" element={<LogMaterialPage />} />
                  <Route path="status" element={<OperatorStatus />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </ModeProvider>
            </OfflineProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
